/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { IMovable } from "../../core/imovable";
import type { IPlayable } from "../../core/iplayable";
import type { Player } from "../../core/player";
import type { PlayerPhysics } from "../../core/player-physics";
import type { Vertex3D } from "../../math/vertex3d";
import type { HitObject } from "../../physics/hit-object";
import type { Table } from "../../table-model";
import type { BallData } from "./ball-data";
import { BallHit } from "./ball-hit";
import type { BallMover } from "./ball-mover";
import type { BallState } from "./ball-state";

/**
 * A ball. The player creates balls at run time (a ball is not a table item).
 */
export class Ball implements IPlayable, IMovable {
  public readonly state: BallState;
  public readonly data: BallData;
  public readonly hit: BallHit;

  // unique ID for each ball. The first ball of a game has id 1.
  public id: number;

  // public props
  get coll() {
    return this.hit.coll;
  }

  // ugly hacks
  public oldVel: Vertex3D;

  constructor(
    id: number,
    data: BallData,
    state: BallState,
    initialVelocity: Vertex3D,
    physics: PlayerPhysics,
    table: Table,
  ) {
    this.id = id;
    this.data = data;
    this.state = state;
    this.oldVel = initialVelocity.clone();
    this.hit = new BallHit(this, this.data, this.state, initialVelocity, table.data, physics.rng);
  }

  public getName(): string {
    return `Ball${this.id}`;
  }

  public getState(): BallState {
    return this.state;
  }

  public getMover(): BallMover {
    return this.hit.getMoverObject();
  }

  /** A ball needs no setup. There is no ball when the player is set up. */
  public setupPlayer(_player?: Player, _table?: Table): void {
    // there is no ball yet on player setup
  }

  public getHitShapes(): HitObject[] {
    return [this.hit];
  }

  public isCollidable(): boolean {
    return true;
  }
}
